import { useState, useEffect, useMemo, useCallback } from 'react';
import { SxStyle } from '../../../types';
import { useStringsContext } from '../../../contexts/strings';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import { useSocial } from './hooks/social';
import { Comment } from './types';
import svStrings from '../../../sv-strings';
import SocialHeader from './components/social-header';
import SocialListCommentToggle from './components/social-list-comment-toggle';
import moment from 'moment';

const contentTypeString: Record<string, string> = {
  news: svStrings.social.contentNews,
};

const styles: SxStyle = {
  dataGridContainer: {
    height: 'calc(100% - 80px)',
    width: '100%',
  },
  dataGrid: (theme) => ({
    '& .MuiDataGrid-root .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer, .MuiDataGrid-columnHeaderTitleContainerContent': {
      width: '100%',
    },
    '& .MuiDataGrid-row:nth-of-type(odd)': {
      backgroundColor: theme.palette.shades.p10,
    },
  }),
};

interface Props {
  selectedAbbrv: string;
}

function SocialCommentsList({ selectedAbbrv }: Props) {
  const { strings } = useStringsContext();
  const [loading, setLoading] = useState(true);
  // const [totalNumberOfComments, setTotalNumberOfComments] = useState(0); // TODO
  const [dataGridRows, setDataGridRows] = useState<Comment[]>([]);
  const { getComments } = useSocial();

  const getCommentsResult = useCallback(async () => {
    try {
      const comments = await getComments(selectedAbbrv);

      if (comments) {
        // const total = results.reduce((acc, result) => acc + result.numberOfReactions, 0);

        setDataGridRows(comments);
        // setTotalNumberOfComments(total);
      } else {
        setDataGridRows([]);
        // setTotalNumberOfComments(0);
      }
    } catch (error) {
      // do nothing
    }

    setLoading(false);
  }, [selectedAbbrv, getComments]);

  useEffect(() => {
    getCommentsResult();
  }, [getCommentsResult]);

  const initialColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'fullName',
        headerName: strings.social.commentsFieldAuthor,
        flex: 1,
        maxWidth: 250,
      },
      {
        field: 'createdAt',
        headerName: strings.social.commentsFieldDate,
        flex: 1,
        maxWidth: 150,
        valueGetter: (value, row) => moment(value).format('YYYY-MM-DD HH:mm'),
      },
      {
        field: 'text',
        headerName: strings.social.commentsFieldComment,
        flex: 1,
      },
      {
        field: 'contentType',
        headerName: strings.social.commentsFieldContentType,
        flex: 1,
        maxWidth: 150,
        valueGetter: (value, row) => contentTypeString[value],
      },
      {
        field: 'hidden',
        headerName: strings.social.commentsFieldShow,
        flex: 1,
        renderCell: (params: GridRenderCellParams) => (
          <SocialListCommentToggle comment={params.row} />
        ),
      },
    ],
    [strings]
  );

  return (
    <>
      <SocialHeader selectedAbbrv={selectedAbbrv} />
      <Paper elevation={2} sx={styles.dataGridContainer}>
        <DataGrid
          sx={styles.dataGrid}
          rows={dataGridRows}
          columns={initialColumns}
          getRowId={(row) => row.id}
          loading={loading}
          autoPageSize
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          initialState={{
            sorting: {
              sortModel: [
                {
                  field: 'createdAt',
                  sort: 'desc',
                },
              ],
            },
          }}
        />
      </Paper>
    </>
  );
}

export default SocialCommentsList;
